<script setup lang="ts">
import type { IRadioInputItem } from "@/components/ui/RadioInput/radioInput.interface"
import type { IReminder, ToastHtmlElement } from "@/types"
import { useMutation } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { ref } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { createReminder } from "@/api"
import { Alert, Button, Hint, Input, RadioInput, Toast } from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestLayout } from "@/layouts"
import { ReminderOptions } from "@/types"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const { t } = useTranslation()

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/reminder", value => Number(value))

const router = useRouter()
const urlParams = useUrlSearchParams<{ album?: number }>("history")
const errorToastRef = ref<ToastHtmlElement | null>(null)
const alertMessage = ref<string>(t("remind_me_later_page.alert"))
const validationErrors = ref()

const whenItems: IRadioInputItem[] = [
  { value: ReminderOptions.Now, label: t("remind_me_later_page.form.now") },
  { value: ReminderOptions.OneDay, label: t("remind_me_later_page.form.one_day") },
  { value: ReminderOptions.OneWeek, label: t("remind_me_later_page.form.one_week") },
]

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().optional(),
    email: z.string().email(t("remind_me_later_page.error.email")),
    when: z.nativeEnum(ReminderOptions),
  }),
)

const { values, handleSubmit, handleReset } = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    when: ReminderOptions.Now,
  },
})

const { mutate, isPending } = useMutation({
  mutationKey: ["createReminder", eventVaultId.value, values.name, values.email, values.when],
  mutationFn: (reminder: IReminder) => createReminder(reminder, eventVaultId.value),
  onSuccess: (message: string) => {
    handleReset()
    router.push(urlParams.album
      ? `/guest/welcome/${eventVaultId.value}?album=${urlParams.album}&message=${message}`
      : `/guest/welcome/${eventVaultId.value}/?message=${message}`,
    )
  },
  onError: (error) => {
    let errorMessage = t("try_again")
    if (error instanceof ValidationException) {
      errorMessage = t("validation_error")
      validationErrors.value = error
    }

    errorToastRef.value?.showToast({ content: errorMessage, title: t("validation_error"), icon: "Caution" })
  },
})

const onSubmit = handleSubmit(({ name, email, when }) => {
  validationErrors.value = undefined
  const reminder = { name, email, when }
  mutate(reminder)
})
</script>

<template>
  <GuestLayout>
    <div class="container gpx-reminder">
      <div>
        <h1 class="gpx-reminder-title heading-subtitle">
          {{ t("remind_me_later_page.title") }}
        </h1>
        <p class="text-gray-700 m-0 pb-2 md:mb-4 gpx-reminder-description">
          {{ t("remind_me_later_page.message") }}
        </p>
        <div class="flex">
          <Alert v-model="alertMessage" variant="info" class="gpx-reminder-alert_desktop" prefix-icon="CircleInfo" :closable="false" />
        </div>
        <Hint class="gpx-reminder-alert_mobile" :hint="alertMessage" />
      </div>
      <div>
        <Transition name="gpx-fade" mode="out-in">
          <div v-if="isPending">
            <div class="gpx-reminder-fields">
              <div class="gpx-skeleton gpx-reminder-input_loading" />
              <div class="gpx-skeleton gpx-reminder-input_loading" />
              <div class="gpx-skeleton gpx-reminder-radio_loading" />
              <div class="gpx-skeleton gpx-reminder-button_loading" />
            </div>
          </div>
          <form v-else @submit.prevent="onSubmit">
            <div class="gpx-reminder-fields">
              <Input name="name" :label="t('remind_me_later_page.form.name')" />
              <Input
                name="email" :label="t('remind_me_later_page.form.email')"
                :error="validationErrors?.firstValidationError('Email')"
              />
              <RadioInput
                name="when" :label="t('remind_me_later_page.form.date')" :items="whenItems"
                :error="validationErrors?.firstValidationError('RemindMeWhen')"
              />
              <div>
                <Button size="lg" data-test="remind-me-submit">
                  {{ t("submit") }}
                </Button>
              </div>
            </div>
          </form>
        </Transition>
      </div>
    </div>
  </GuestLayout>

  <Toast ref="errorToastRef" variant="danger" :duration="10000" />
</template>

<style scoped>
.gpx-reminder {
  padding-top: 1rem;
  padding-bottom: 3rem;
}

.gpx-reminder-title {
  margin: 1rem 0;
  padding: 0;
  font-weight: var(--gpx-font-bold);
}

.gpx-reminder-alert_desktop {
  display: none;
}

.gpx-reminder-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gpx-reminder-input_loading {
  height: 3.5rem;
}

.gpx-reminder-radio_loading {
  height: 3.5rem;
}

.gpx-reminder-button_loading {
  width: 12rem;
  height: 3rem;
}

@media (width >=768px) {
  .gpx-reminder {
    padding-top: 5rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .gpx-reminder-title {
    margin: 0 0 1.5rem;
  }

  .gpx-reminder-alert_mobile {
    display: none;
  }

  .gpx-reminder-alert_desktop {
    display: flex;
    flex-shrink: 1;
    flex-grow: 0;
  }
}
</style>
